import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const TABS = [
  { id: 'inbox', label: 'Inbox', accent: 'emerald' },
  { id: 'requests', label: 'Requests', accent: 'amber' },
  { id: 'spam', label: 'Spam likely', accent: 'rose' }
];

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const diff = Date.now() - date.getTime();
  if (diff < 60_000) return 'now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return date.toLocaleDateString();
};

const normalizeConversation = (raw = {}, fallbackStatus = 'inbox') => ({
  id: raw.id || raw.conversation_id || `${fallbackStatus}-${raw.user_id || raw.recipient_id || raw.username}`,
  userId: raw.user_id || raw.recipient_id || raw.other_user_id,
  username: raw.username || raw.handle || raw.user?.username || 'unknown',
  displayName: raw.display_name || raw.name || raw.user?.display_name || raw.username || 'Unknown',
  avatarUrl: raw.avatar_url || raw.user?.avatar_url || null,
  lastMessage: raw.last_message || raw.preview?.content || '',
  lastTimestamp: raw.last_timestamp || raw.updated_at || raw.preview?.created_at || new Date().toISOString(),
  unreadCount: raw.unread_count ?? raw.unreadCount ?? 0,
  isFollowing: raw.is_following ?? true,
  status: raw.status || fallbackStatus,
  previewMessages: raw.preview_messages || [],
  mentionContext: raw.mention_context || null,
  lastAuthor: raw.last_author || raw.preview?.author || null
});

const normalizeDirectMessage = (raw = {}, currentUserId) => ({
  id: raw.id || raw.message_id || `local-${Math.random().toString(36).slice(2)}`,
  senderId: raw.user_id || raw.sender_id || raw.author_id,
  senderUsername: raw.sender_username || raw.username || raw.author?.username || 'anonymous',
  senderName: raw.sender_name || raw.author?.display_name || raw.sender_username || 'Anonymous',
  content: raw.content || raw.body || '',
  createdAt: raw.created_at || raw.timestamp || new Date().toISOString(),
  isSystem: (raw.message_type || raw.type) === 'system',
  attachments: raw.attachments || [],
  outbound: currentUserId ? (raw.user_id || raw.sender_id) === currentUserId : false
});

const buildMockConversations = (bucket) => {
  const seed = bucket === 'requests'
    ? [
        {
          user_id: 42,
          username: 'fieldagent',
          display_name: 'Field Agent',
          last_message: 'Mind if I loop you into the wildfire dataset thread?',
          last_timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
          unread_count: 2,
          is_following: false,
          status: 'requests'
        }
      ]
    : bucket === 'spam'
    ? [
        {
          user_id: 404,
          username: 'promo-drop',
          display_name: 'Promo Drop',
          last_message: 'Win 10k tokens by clicking here',
          last_timestamp: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
          unread_count: 0,
          is_following: false,
          status: 'spam'
        }
      ]
    : [
        {
          user_id: 1,
          username: 'gh0st',
          display_name: 'gh0st',
          last_message: 'New drop is live. need your verdict asap.',
          last_timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
          unread_count: 0,
          is_following: true,
          status: 'inbox'
        },
        {
          user_id: 7,
          username: 'spectralops',
          display_name: 'Spectral Ops',
          last_message: 'Uploading scan results now.',
          last_timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          unread_count: 4,
          is_following: true,
          status: 'inbox'
        }
      ];
  return seed.map((item) => normalizeConversation(item, bucket));
};

const buildMockMentions = () => [
  {
    id: 'mention-1',
    community: 'Signal Desk',
    channel: '#intel-drops',
    excerpt: '@you can you confirm if this clip is watermarked? Thread is hot.',
    timestamp: new Date(Date.now() - 1000 * 60 * 20).toISOString(),
    deepLink: '/communities/signal-desk',
    context: 'Thread linked from signal-desk/intel-drops'
  },
  {
    id: 'mention-2',
    community: 'Watermark Lab',
    channel: '#triage',
    excerpt: 'Need your take on the EXIF mismatch you flagged earlier.',
    timestamp: new Date(Date.now() - 1000 * 60 * 55).toISOString(),
    deepLink: '/communities/watermark-lab/triage',
    context: 'Triggered automatically because you are a member'
  }
];

export default function Messages() {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('inbox');
  const [conversations, setConversations] = useState({ inbox: [], requests: [], spam: [] });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [composerValue, setComposerValue] = useState('');
  const [requestsUnread, setRequestsUnread] = useState(0);
  const [error, setError] = useState(null);

  const fetchMentions = useCallback(async () => {
    try {
      const data = await apiService.getNotifications({ limit: 25, offset: 0, unread_only: false });
      const list = Array.isArray(data?.notifications) ? data.notifications : Array.isArray(data) ? data : [];
      const mentionItems = list
        .filter((item) => (item.type || item.category || '').toLowerCase().includes('mention'))
        .map((item) => ({
          id: item.id || item.notification_id || `mention-${Date.now()}`,
          community: item.metadata?.community_name || item.community || 'Community',
          channel: item.metadata?.channel_name || item.channel || '#thread',
          excerpt: item.metadata?.excerpt || item.message || item.title || '',
          timestamp: item.created_at || item.timestamp || new Date().toISOString(),
          deepLink: item.metadata?.deep_link || `/communities/${item.metadata?.community_slug || ''}`,
          context: item.metadata?.context || 'Mention routed to your inbox'
        }));
      setMentions(mentionItems.length ? mentionItems : buildMockMentions());
    } catch (err) {
      console.warn('Mention fetch unavailable, using mock data', err.message);
      setMentions(buildMockMentions());
    }
  }, []);

  useEffect(() => {
    fetchMentions();
  }, [fetchMentions]);

  const loadBucket = useCallback(async (bucket) => {
    try {
      const payload = await apiService.getDirectInbox({ tab: bucket, limit: 50, offset: 0 });
      const list = Array.isArray(payload?.conversations)
        ? payload.conversations
        : Array.isArray(payload)
        ? payload
        : payload?.data || [];
      return list.map((item) => normalizeConversation(item, bucket));
    } catch (err) {
      console.warn(`Inbox ${bucket} not ready, using mock data`, err.message);
      return buildMockConversations(bucket);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    if (!isAuthenticated) {
      setConversations({ inbox: [], requests: [], spam: [] });
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    try {
      const [inbox, requests, spam] = await Promise.all([
        loadBucket('inbox'),
        loadBucket('requests'),
        loadBucket('spam')
      ]);
      setConversations({ inbox, requests, spam });
      setRequestsUnread(requests.reduce((sum, convo) => sum + (convo.unreadCount || 0), 0));
      if (!selectedConversation && inbox.length) {
        setSelectedConversation(inbox[0]);
      }
    } finally {
      setLoadingList(false);
    }
  }, [isAuthenticated, loadBucket, selectedConversation]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!searchQuery?.trim() || searchQuery.trim().length < 2) {
      setUserSearchResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await apiService.searchUsers(searchQuery.trim(), { limit: 5, offset: 0 });
        if (!cancelled) {
          const list = Array.isArray(results?.users) ? results.users : Array.isArray(results) ? results : [];
          setUserSearchResults(list);
        }
      } catch (err) {
        if (!cancelled) setUserSearchResults([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery]);

  const filteredConversations = useMemo(() => {
    const bucket = conversations[activeTab] || [];
    if (!searchQuery.trim()) {
      return [...bucket].sort((a, b) => new Date(b.lastTimestamp) - new Date(a.lastTimestamp));
    }
    const query = searchQuery.trim().toLowerCase();
    return bucket
      .filter((item) => {
        const haystack = [item.username, item.displayName, item.lastMessage]
          .filter(Boolean)
          .join(' ') + ' ' + item.previewMessages.map((m) => m.content).join(' ');
        return haystack.toLowerCase().includes(query);
      })
      .sort((a, b) => new Date(b.lastTimestamp) - new Date(a.lastTimestamp));
  }, [conversations, activeTab, searchQuery]);

  const loadThread = useCallback(async (conversation) => {
    if (!conversation?.userId) {
      setMessages([]);
      return;
    }

    // Skip fetch for drafts
    if (String(conversation.id).startsWith('draft-')) {
      setMessages([]);
      return;
    }

    setLoadingThread(true);
    setError(null);
    try {
      const data = await apiService.getDirectMessages(conversation.id, { limit: 200, offset: 0 });
      const list = Array.isArray(data?.messages) ? data.messages : Array.isArray(data) ? data : [];
      const normalized = list.map((msg) => normalizeDirectMessage(msg, user?.id));
      if (normalized.length) {
        setMessages(normalized);
      } else {
        const fallback = (conversation.previewMessages || []).map((msg) =>
          normalizeDirectMessage(
            {
              id: msg.id,
              user_id: msg.user_id,
              content: msg.content,
              created_at: msg.created_at
            },
            user?.id
          )
        );
        setMessages(fallback);
      }
    } catch (err) {
      console.warn('Thread fetch failed, using cached preview', err.message);
      setError('Live thread unavailable. Showing cached preview.');
      const fallback = (conversation.previewMessages || conversation.localDraft || []).map((msg) =>
        normalizeDirectMessage(msg, user?.id)
      );
      setMessages(fallback);
    } finally {
      setLoadingThread(false);
    }
  }, [user?.id]);

  const handleConversationSelect = useCallback(
    (conversation) => {
      setSelectedConversation(conversation);
      if (conversation) {
        setActiveTab(conversation.status || 'inbox');
        loadThread(conversation);
      }
    },
    [loadThread]
  );

  const handleSendMessage = async (event) => {
    event?.preventDefault();
    if (!composerValue.trim() || !selectedConversation) return;
    
    // Validate recipient exists before attempting to send
    const recipientId = selectedConversation.userId || selectedConversation.user_id || selectedConversation.recipient_id;
    if (!recipientId) {
      setError('Cannot send message: recipient not found');
      console.warn('❌ Missing recipient ID in conversation:', selectedConversation);
      return;
    }
    
    const pending = normalizeDirectMessage(
      {
        id: `pending-${Date.now()}`,
        user_id: user?.id,
        content: composerValue,
        created_at: new Date().toISOString()
      },
      user?.id
    );
    setMessages((prev) => [...prev, pending]);
    setComposerValue('');

    try {
      const response = await apiService.sendDirectMessage(recipientId, composerValue.trim());
      if (response) {
        const normalized = normalizeDirectMessage(response.message || response, user?.id);
        setMessages((prev) => prev.map((msg) => (msg.id === pending.id ? normalized : msg)));
      }
    } catch (err) {
      setMessages((prev) => prev.filter((msg) => msg.id !== pending.id));
      setComposerValue(composerValue.trim());
      setError(err.message || 'Failed to send message');
    }
  };

  const mutateConversationBuckets = (conversation, from, to) => {
    setConversations((prev) => {
      const next = { ...prev };
      next[from] = prev[from].filter((c) => c.id !== conversation.id);
      next[to] = [normalizeConversation({ ...conversation, status: to }), ...prev[to]];
      return next;
    });
    if (from === 'requests') {
      setRequestsUnread((prev) => Math.max(prev - (conversation.unreadCount || 0), 0));
    }
  };

  const handleRequestAction = async (conversation, action) => {
    if (!conversation) return;
    try {
      if (action === 'accept') {
        await apiService.respondToMessageRequest(conversation.id, 'accept');
        mutateConversationBuckets(conversation, 'requests', 'inbox');
        setSelectedConversation({ ...conversation, status: 'inbox' });
      } else if (action === 'decline') {
        await apiService.respondToMessageRequest(conversation.id, 'decline');
        mutateConversationBuckets(conversation, 'requests', 'requests');
        setSelectedConversation(null);
      } else if (action === 'spam') {
        await apiService.markConversationSpam(conversation.id, { isSpam: true });
        mutateConversationBuckets(conversation, conversation.status === 'requests' ? 'requests' : 'inbox', 'spam');
        setSelectedConversation(null);
      }
    } catch (err) {
      console.warn('Request action pending backend support:', err.message);
    }
  };

  const handleStartConversation = (userRecord) => {
    const conversation = normalizeConversation(
      {
        id: `draft-${userRecord.id}`,
        user_id: userRecord.id,
        username: userRecord.username,
        display_name: userRecord.display_name || userRecord.username,
        last_message: 'Draft conversation',
        last_timestamp: new Date().toISOString(),
        unread_count: 0,
        is_following: true,
        status: 'inbox'
      },
      'inbox'
    );

    setConversations((prev) => {
      const exists = prev.inbox.some((c) => c.userId === conversation.userId);
      if (exists) return prev;
      return { ...prev, inbox: [conversation, ...prev.inbox] };
    });
    setSelectedConversation(conversation);
    setActiveTab('inbox');
    setMessages([]);
    setSearchQuery('');
    setUserSearchResults([]);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center gap-6 text-center px-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/40">Encrypted relay</p>
          <h1 className="text-4xl font-black mt-4">Sign in to unlock direct messages</h1>
          <p className="text-white/60 mt-4 max-w-xl">
            Secure DMs, message requests, and @mention routing live inside this console. Log in to sync conversations across devices.
          </p>
        </div>
        <button
          onClick={() => (window.location.href = '/login')}
          className="px-8 py-3 rounded-full bg-emerald-500 text-slate-950 font-bold"
        >
          Go to login
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/40">Encrypted relay</p>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-black mt-2">Messages</h1>
            <span className="text-xs uppercase tracking-[0.3em] text-emerald-300/70 mt-2">
              {requestsUnread} requests awaiting review
            </span>
          </div>
          <p className="text-white/60 mt-2 max-w-3xl">
            Search across conversations, drop new intel, and triage spam. Message requests stay silent, but we tally unread counts so nothing slips.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[300px,minmax(0,1fr),240px]">
          {/* Conversation list */}
          <aside className="space-y-4">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-4">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users, messages, keywords"
                className="w-full rounded-2xl bg-black/40 border border-white/10 px-4 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              />
              {userSearchResults.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs uppercase tracking-[0.3em] text-white/40">Start a conversation</p>
                  <div className="space-y-1">
                    {userSearchResults.map((profile) => (
                      <button
                        key={profile.id || profile.username}
                        onClick={() => handleStartConversation(profile)}
                        className="w-full text-left px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 transition"
                      >
                        <p className="text-sm font-semibold">{profile.display_name || profile.username}</p>
                        <p className="text-xs text-white/50">@{profile.username}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 p-3">
              <div className="grid grid-cols-3 gap-2">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`rounded-2xl px-3 py-2 text-xs font-semibold transition ${
                      activeTab === tab.id
                        ? 'bg-white text-slate-900'
                        : 'bg-white/5 text-white/70 hover:text-white'
                    }`}
                  >
                    {tab.label}
                    {tab.id === 'requests' && requestsUnread > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center text-[10px] px-2 py-0.5 rounded-full bg-amber-400/30 text-amber-200">
                        {requestsUnread}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {activeTab === 'requests' && (
                <p className="mt-3 text-xs text-amber-200/80">
                  Message requests stay quiet: no push notifications, just the unread tally above. Review and accept to move the thread into your inbox.
                </p>
              )}
              {activeTab === 'spam' && (
                <p className="mt-3 text-xs text-rose-200/80">
                  Spam likely threads auto-delete after 30 days. Mark legit conversations as &ldquo;Not spam&rdquo; to return them to inbox.
                </p>
              )}
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 max-h-[55vh] overflow-y-auto">
              {loadingList ? (
                <div className="p-6 text-center text-white/60 text-sm">Loading conversations…</div>
              ) : filteredConversations.length === 0 ? (
                <div className="p-6 text-center text-white/60 text-sm">No conversations match that filter.</div>
              ) : (
                filteredConversations.map((conversation) => (
                  <button
                    key={conversation.id}
                    onClick={() => handleConversationSelect(conversation)}
                    className={`w-full flex items-start gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/5 text-left transition ${
                      selectedConversation?.id === conversation.id ? 'bg-white/10' : ''
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center font-bold">
                      {conversation.displayName[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold truncate">{conversation.displayName}</p>
                        <span className="text-xs text-white/40">{formatRelativeTime(conversation.lastTimestamp)}</span>
                      </div>
                      <p className="text-xs text-white/60 truncate">{conversation.lastMessage || 'No messages yet'}</p>
                      {conversation.unreadCount > 0 && (
                        <span className="inline-flex mt-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-200">
                          {conversation.unreadCount} unread
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </aside>

          {/* Thread view */}
          <section className="rounded-3xl border border-white/10 bg-white/5 flex flex-col min-h-[60vh]">
            {selectedConversation ? (
              <>
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                  <div>
                    <p className="text-sm font-semibold">{selectedConversation.displayName}</p>
                    <p className="text-xs text-white/50">@{selectedConversation.username}</p>
                  </div>
                  {selectedConversation.status === 'requests' && (
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        onClick={() => handleRequestAction(selectedConversation, 'accept')}
                        className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleRequestAction(selectedConversation, 'decline')}
                        className="px-3 py-1 rounded-full bg-white/10 text-white/70 hover:bg-white/20"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => handleRequestAction(selectedConversation, 'spam')}
                        className="px-3 py-1 rounded-full bg-rose-500/20 text-rose-200 hover:bg-rose-500/30"
                      >
                        Mark spam
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                  {loadingThread ? (
                    <div className="text-center text-white/60 text-sm">Loading thread…</div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-white/60 text-sm">No messages yet. Say hi!</div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.outbound ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm ${
                            msg.outbound ? 'bg-emerald-500/20 text-white' : 'bg-white/10 text-white'
                          }`}
                        >
                          {!msg.outbound && (
                            <p className="text-xs text-white/60 mb-1">{msg.senderName}</p>
                          )}
                          <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                          <span className="block text-[10px] text-white/40 mt-1">{formatRelativeTime(msg.createdAt)}</span>
                        </div>
                      </div>
                    ))
                  )}
                  {error && <p className="text-xs text-amber-300">{error}</p>}
                </div>

                <form onSubmit={handleSendMessage} className="border-t border-white/5 px-5 py-4">
                  <div className="flex items-end gap-3">
                    <textarea
                      value={composerValue}
                      onChange={(e) => setComposerValue(e.target.value)}
                      placeholder="Drop a message…"
                      rows={2}
                      className="flex-1 rounded-2xl bg-black/30 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                    />
                    <button
                      type="submit"
                      disabled={!composerValue.trim()}
                      className="px-5 py-3 rounded-2xl bg-purple-500 text-white font-semibold disabled:opacity-50"
                    >
                      Send
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
                <p className="text-lg font-semibold">Pick a conversation to get started</p>
                <p className="text-white/60 text-sm">Or search for a user above to spin up a new thread.</p>
              </div>
            )}
          </section>

          {/* Mentions & metadata */}
          <section className="space-y-4">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">@ Mentions</p>
                <span className="text-xs text-white/40">Synced from communities</span>
              </div>
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {mentions.map((mention) => (
                  <div key={mention.id} className="rounded-2xl border border-white/10 bg-black/20 p-3 text-sm">
                    <div className="flex items-center justify-between text-xs text-white/50">
                      <span>{mention.community} · {mention.channel}</span>
                      <span>{formatRelativeTime(mention.timestamp)}</span>
                    </div>
                    <p className="mt-2 text-white/80 text-sm">{mention.excerpt}</p>
                    <p className="text-[11px] text-white/40 mt-1">{mention.context}</p>
                    <div className="mt-2 flex gap-2">
                      <a
                        href={mention.deepLink}
                        className="text-xs px-3 py-1 rounded-full bg-white/10 hover:bg-white/20"
                      >
                        View thread
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedConversation) {
                            setComposerValue((prev) => `${prev ? prev + '\n' : ''}@${mention.channel.replace('#', '')} `);
                          }
                        }}
                        className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-200"
                      >
                        Respond here
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 p-4 text-xs text-white/60 space-y-2">
              <p className="uppercase tracking-[0.3em] text-white/40">Rules of engagement</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Requests stay mute until you accept them.</li>
                <li>Spam likely purges itself after 30 days.</li>
                <li>Mentions only appear here if you’re inside that community.</li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
